import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Search, 
  Globe, 
  Star,
  LogOut,
  Menu,
  X,
  Send,
  ShoppingCart,
  Clock,
  BarChart3,
  MapPin,
  AlertCircle,
  Map,
  FileText
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminLayout = () => {
  const { user, loading, logout, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [counts, setCounts] = useState({});
  const location = useLocation();

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch sidebar counts
  useEffect(() => {
    const fetchCounts = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/sidebar-counts`, { headers });
        setCounts(response.data);
      } catch (error) {
        console.error('Failed to fetch sidebar counts:', error);
      }
    };
    fetchCounts();
    
    // Refresh counts every 30 seconds
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [token, location.pathname]); // Refresh on route change too

  // Overview section (Dashboard & API Usage)
  const overviewLinks = [
    { name: 'Dashboard', path: '/admin/dashboard', icon: LayoutDashboard },
    { name: 'API Usage', path: '/admin/api-usage', icon: BarChart3 },
    { name: 'Coverage', path: '/admin/coverage', icon: MapPin },
    { name: 'Postcodes', path: '/admin/postcodes', icon: Map },
    { name: 'Blog', path: '/admin/blog', icon: FileText },
  ];

  // Workflow section (business pipeline)
  const workflowLinks = [
    { name: 'Find Businesses', path: '/admin/find', icon: Search },
    { name: 'Snoozed', path: '/admin/snoozed', icon: Clock, countKey: 'snoozed' },
    { name: 'To Action', path: '/admin/favourites', icon: Star, countKey: 'favourites' },
    { name: 'Generated Websites', path: '/admin/websites', icon: Globe, countKey: 'websites' },
    { name: 'Preview Sent', path: '/admin/preview-sent', icon: Send, countKey: 'preview_sent' },
    { name: 'No Response', path: '/admin/no-response', icon: AlertCircle, countKey: 'no_response' },
    { name: 'Orders', path: '/admin/ordered', icon: ShoppingCart, countKey: 'ordered' },
    { name: 'Live', path: '/admin/live', icon: Globe, countKey: 'live' },
  ];

  // Combined for page title lookup
  const allLinks = [...overviewLinks, ...workflowLinks];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!user || !user.is_admin) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar - Fixed */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-screen">
          {/* Logo */}
          <div className="p-6 border-b border-border flex items-center justify-between flex-shrink-0">
            <Link to="/admin/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                <Globe className="w-5 h-5 text-primary" />
              </div>
              <span className="font-bold">TryWeb Admin</span>
            </Link>
            <button 
              onClick={() => setSidebarOpen(false)} 
              className="lg:hidden p-1 hover:bg-white/5 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Nav Links */}
          <nav className="flex-1 p-4 overflow-y-auto">
            {/* Overview Section */}
            <div className="space-y-1">
              {overviewLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path || (link.path === '/admin/dashboard' && location.pathname === '/admin');
                const count = link.countKey ? counts[link.countKey] : null;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`sidebar-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-primary bg-primary/10'
                        : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="flex-1">{link.name}</span>
                    {count > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        isActive 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {count}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Separator */}
            <div className="my-4 border-t border-border" />

            {/* Workflow Section */}
            <div className="space-y-1">
              <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Pipeline
              </p>
              {workflowLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.path;
                const count = link.countKey ? counts[link.countKey] : null;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`sidebar-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-primary bg-primary/10'
                        : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="flex-1">{link.name}</span>
                    {count > 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        isActive 
                          ? 'bg-primary text-primary-foreground' 
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {count}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* User - Fixed at bottom */}
          <div className="p-4 border-t border-border flex-shrink-0">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-sm font-medium">{user.name?.charAt(0).toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full justify-start gap-2"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content - With left margin to account for fixed sidebar */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-16 border-b border-border bg-card/50 backdrop-blur-sm flex items-center px-4 lg:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 -ml-2 hover:bg-white/5 rounded-lg"
            data-testid="mobile-sidebar-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold ml-2 lg:ml-0">
            {allLinks.find(l => l.path === location.pathname)?.name || 'Dashboard'}
          </h1>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
